import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import { ArrowDropUp, ArrowDropDown } from "@mui/icons-material";

import axios from "../../utils/axiosInstance";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function InvalidTable({ device }) {
  const [data, setData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  // fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!device?.id) return;
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/device-registers/${device.id}/20`
        );
        if (!response)
          throw new Error(`HTTP error! status: ${response.status}`);
        setData(response.data.response);
      } catch (err) {
        console.error("Error fetching engine data:", err);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [device?.id]);

  // set rows for table
  const getRows = () => {
    if (!Array.isArray(data) || data.length === 0) return [];

    return data.map((item) => ({
      __time: item.time, // ✅ raw unix timestamp
      Date: new Date(item.time * 1000).toLocaleString(),
      State: item.type ?? "N/A",
    }));
  };

  // filter + sort
  const filteredRows = React.useMemo(() => {
    let rows = getRows().filter((row) =>
      Object.values(row).some((val) =>
        String(val).toLowerCase().includes(searchText.toLowerCase())
      )
    );

    if (sortConfig.key) {
      rows.sort((a, b) => {
        let valA, valB;

        if (sortConfig.key === "Date") {
          valA = a.__time;
          valB = b.__time;
        } else {
          valA = a[sortConfig.key];
          valB = b[sortConfig.key];
        }

        if (valA == null) return 1;
        if (valB == null) return -1;

        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return rows;
  }, [data, searchText, sortConfig]);
  // sorting function
  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  // export helpers
  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Invalid_Table_${device?.id || "device"}.xlsx`);
  };

  const handleDownloadCSV = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `Invalid_Table_${device?.id || "device"}.csv`);
  };

  const header = ["Date", "State"];
  const handleDownloadPDF = () => {
    const rows = filteredRows;
    const doc = new jsPDF({ orientation: "portrait" });
    doc.text(`Invalid Table For Device : ${device?.id}`, 14, 10);
    autoTable(doc, {
      head: [header],
      body: rows.map((row) => header.map((h) => row[h])),
      startY: 20,
      styles: { fontSize: 6 },
    });
    doc.save(`Invalid_Table_${device?.id || "device"}.pdf`);
  };

  return (
    <div className="bg-slate-800 p-3 sm:p-4 rounded-xl shadow-lg">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-slate-700 pb-2 mb-4 gap-2">
        <h2 className="text-slate-200 font-semibold text-base sm:text-lg text-center sm:text-left">
          {device?.name || "Device"} - Invalid Data Table
        </h2>
      </div>

      {/* Search + Actions */}
      <div className="px-2 sm:px-4 py-3 bg-slate-900 border-b border-slate-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="
          w-full sm:w-64
          bg-slate-900 border border-slate-600
          rounded px-2 py-1.5 text-sm
          text-slate-200 placeholder-slate-400
          focus:outline-none focus:ring-1 focus:ring-blue-500
        "
        />

        <div className="flex flex-wrap sm:flex-nowrap gap-2 justify-center sm:justify-end">
          <button
            onClick={handleDownloadExcel}
            className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm px-3 py-1.5 rounded"
          >
            Excel
          </button>
          <button
            onClick={handleDownloadCSV}
            className="flex-1 sm:flex-none bg-blue-600 hover:bg-green-700 text-white text-xs sm:text-sm px-3 py-1.5 rounded"
          >
            CSV
          </button>
          <button
            onClick={handleDownloadPDF}
            className="flex-1 sm:flex-none bg-blue-600 hover:bg-red-700 text-white text-xs sm:text-sm px-3 py-1.5 rounded"
          >
            PDF
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto mt-2">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="border-b border-slate-700 bg-slate-900">
              {["Date", "State"].map((header) => (
                <th
                  key={header}
                  onClick={() =>
                    setSortConfig((prev) => ({
                      key: header,
                      direction:
                        prev.key === header && prev.direction === "asc"
                          ? "desc"
                          : "asc",
                    }))
                  }
                  className="
                  px-2 sm:px-3 py-2
                  text-[10px] sm:text-xs
                  font-semibold text-slate-400
                  text-left cursor-pointer select-none
                  whitespace-nowrap
                "
                >
                  {header}{" "}
                  {sortConfig.key === header
                    ? sortConfig.direction === "asc"
                      ? "▲"
                      : "▼"
                    : "↕"}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={2}
                  className="text-center py-4 text-slate-400 text-sm"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredRows.length > 0 ? (
              filteredRows.map((row, index) => (
                <tr
                  key={index}
                  className={`text-gray-200 transition-colors ${
                    index % 2 === 0
                      ? "bg-slate-800 hover:bg-slate-700"
                      : "bg-slate-900 hover:bg-slate-700"
                  }`}
                >
                  <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-left whitespace-nowrap">
                    {row.Date}
                  </td>
                  <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-left whitespace-nowrap">
                    {row.State}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={2}
                  className="text-center py-4 text-slate-400 italic text-sm"
                >
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
